"use client";

import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  AdminPagination,
  PanelLoader,
  Panel,
  PanelHeader,
  PanelBody,
  PanelError,
  getFileUrl,
} from "@/shared";
import { adminWithdrawalList, UserProvider, UserRole } from "@/entities";
import { AdminWithdrawalsFilter } from "@/features";

export function AdminWithdrawalsList() {
  const searchParams = useSearchParams();

  const keyword = searchParams?.get("keyword") || "";
  const page = parseInt(searchParams?.get("page") || "1");

  const perPage = 15;
  const filterParams = { keyword };

  const { error, isLoading, data } = useQuery({
    queryKey: [`admin-withdrawals`, { ...filterParams, page, perPage }],
    queryFn: () => adminWithdrawalList({ ...filterParams, page, perPage }),
  });

  if (error) {
    return <PanelError error={error} />;
  }

  if (isLoading) {
    return <PanelLoader panelTitle="목록(0건)" />;
  }

  return (
    <>
      <Panel
        queryKey={[`admin-withdrawals`, { ...filterParams, page, perPage }]}
      >
        <PanelHeader>목록{!isLoading && `(${data?.count}건)`}</PanelHeader>
        <PanelBody>
          <AdminWithdrawalsFilter />
          {data?.count && data?.count > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle text-center text-nowrap">
                  <thead>
                    <tr>
                      <th>권한</th>
                      <th>가입</th>
                      <th>회원 정보</th>
                      <th>탈퇴</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.result.map((item: any, index: number) => {
                      return (
                        <tr key={index}>
                          <td>{UserProvider(item.provider)}</td>
                          <td>{UserRole(item.role)}</td>
                          <td className="text-start">
                            <div className="d-flex align-items-center">
                              <div className="w-50px h-50px bg-light d-flex align-items-center justify-content-center">
                                <Image
                                  src={
                                    item.cover_src
                                      ? getFileUrl(item.cover_src)
                                      : "/assets/images/default_user.png"
                                  }
                                  className="mw-100 mh-100"
                                  width={50}
                                  height={50}
                                  alt="프로필 이미지"
                                />
                              </div>
                              <div className="ms-3">
                                <div>
                                  이름 : {item.name}{" "}
                                  {moment()
                                    .add(-1, "days")
                                    .isBefore(moment(item.created_at)) && (
                                    <span className="badge bg-dark">New</span>
                                  )}
                                </div>
                                <div>이메일 : {item.email}</div>
                                <div>휴대번화 : {item.phone}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            {new Date(item.created_at).toLocaleDateString("ko")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="d-md-flex align-items-center justify-content-between">
                <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
                  {data.count}개 항목 중 {(page - 1) * perPage + 1}에서{" "}
                  {data.count < (page - 1) * perPage + perPage
                    ? data.count
                    : (page - 1) * perPage + perPage}
                  까지 표시
                </div>
                <AdminPagination
                  total={Math.ceil(data.count / perPage)}
                  page={page}
                  pathname="/admin/withdrawals"
                  params={filterParams}
                />
              </div>
            </>
          ) : (
            <p className="m-5 text-center">등록된 데이터가 없습니다.</p>
          )}
        </PanelBody>
      </Panel>
    </>
  );
}
